import { Component, OnDestroy, OnInit } from '@angular/core'
import { AuthService } from '../../singleton-services/auth/auth.service'
import type { IUser, IUserIdentity } from '../../singleton-services/auth/auth.interfaces'
import { SegmentService } from '../../shared/analytics/services/segment.service'
import { SegmentEvents } from '../../shared/analytics/models/segment-events.model'
import { LoggingService } from 'src/app/singleton-services/logging/logging.service'
import { ActivatedRoute } from '@angular/router'
import { map, take, takeUntil, tap, catchError, filter } from 'rxjs/operators'
import { combineLatest, Observable, of, Subject } from 'rxjs'
import { FunnelHeaderPromptStatus } from '../shared/funnel-checkout-header/funnel-checkout-header.component'
import { VerticalService } from '../../singleton-services/vertical.service'
import { CheckoutNavigationService } from '../../shared/services/checkout-navigation.service'
import { FloQueryParamsService } from '../../shared/queryparams/queryparams.service'
import { createGenericCanonicalUrl } from '../../singleton-services/seo/seo.utility'
import { GoogleAuthError, GoogleAuthService } from 'src/app/shared/services/google-auth.service'
import { isNewUser } from 'src/app/singleton-services/auth/auth.utility'
import { SeoService } from '../../singleton-services/seo/seo.service'
import { PlatformService } from '../../singleton-services/platform.service'
import { CredentialResponse } from 'google-one-tap'
import { AccountService } from 'src/app/singleton-services/account.service'
import { SiteSettings } from 'src/app/shared/models/site-settings.model'
import { ImpactService } from 'src/app/shared/services/impact.service'
import { SupplementedUserSubscription } from 'src/app/account-settings/account-settings.interfaces'
import { MinimalSubscription } from 'src/app/shared/subscriptions/subscriptions.interface'
import { CookieService } from 'src/app/singleton-services/cookie.service'

@Component({
  selector: 'flo-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  constructor(
    private auth: AuthService,
    private checkoutNavService: CheckoutNavigationService,
    private cookieService: CookieService,
    private loggingService: LoggingService,
    private segmentService: SegmentService,
    private ar: ActivatedRoute,
    private verticalService: VerticalService,
    private queryParamsService: FloQueryParamsService,
    private googleAuthService: GoogleAuthService,
    private seoService: SeoService,
    private accountService: AccountService,
    private readonly platformService: PlatformService,
    private readonly impactService: ImpactService
  ) {}
  private readonly ngOnDestroy$ = new Subject<void>()
  private readonly googleAuthErrorSubject = new Subject<GoogleAuthError | undefined>()
  public googleSubmitting = false
  public googleAuthError$: Observable<GoogleAuthError | undefined> = this.googleAuthErrorSubject.pipe(
    takeUntil(this.ngOnDestroy$)
  )
  private canonicalURL$ = this.verticalService.siteSettings$.pipe(
    map((siteSettings: SiteSettings) => {
      const liveEvent = this.ar.snapshot.data && this.ar.snapshot.data.liveEvent
      return createGenericCanonicalUrl('login', siteSettings, liveEvent)
    }),
    take(1)
  )
  public config$ = combineLatest([this.verticalService.siteSettings$, this.queryParamsService.queryParams$]).pipe(
    map(([settings, queryParams]) => {
      return {
        showSignupPrompt: FunnelHeaderPromptStatus.SHOW_SIGNUP,
        settings,
        queryParams
      }
    })
  )

  public siteId$ = this.verticalService.siteId$

  public handleEmailLoginSuccess(user?: IUserIdentity) {
    this.segmentService.track(SegmentEvents.ACCOUNT_LOGGED_IN, {
      email: user?.email
    })

    combineLatest([this.auth.user$, this.accountService.allUserSubscriptions$])
      .pipe(take(1))
      .subscribe(
        ([authUser, allUserSubscriptions]) => {
          this.handleAfterLogIn(authUser, allUserSubscriptions)
        },
        catchError(err => {
          this.loggingService.error('Issue Logging In: ', err.message, err.error)
          return of(undefined)
        })
      )
  }

  public handleGoogleLoginSuccess(user: IUserIdentity, googleLogin?: boolean) {
    this.segmentService.track(SegmentEvents.ACCOUNT_LOGGED_IN, {
      email: user.email,
      google_social: googleLogin
    })

    combineLatest([this.auth.user$, this.accountService.allUserSubscriptions$])
      .pipe(take(1))
      .subscribe(([authUser, allUserSubscriptions]) => {
        this.handleAfterLogIn(authUser, allUserSubscriptions)
      })
  }

  /**
   * Segment track call is fired in the facebook auth component
   */
  public handleFacebookLoginSuccess() {
    combineLatest([this.auth.user$, this.accountService.allUserSubscriptions$])
      .pipe(take(1))
      .subscribe(([authUser, allUserSubscriptions]) => {
        this.handleAfterLogIn(authUser, allUserSubscriptions)
      })
  }

  public handleAfterLogIn(
    authUser: IUser,
    allUserSubscriptions: (SupplementedUserSubscription | MinimalSubscription)[]
  ) {
    if (allUserSubscriptions.length > 0) {
      this.impactService.setCustomerInfo(
        allUserSubscriptions[0].customer_id ?? '',
        authUser?.analyticsData?.email ?? ''
      )
      this.cookieService.set('subscription_start_date', allUserSubscriptions[0].purchase_date)
    }

    this.checkoutNavService.navigateAfterLogin(
      authUser.loggedIn,
      allUserSubscriptions.length === 0,
      this.ar.snapshot.queryParams,
      this.ar.snapshot.data.liveEvent
    )
  }

  public googleAuthKey$ = this.googleAuthService.googleAuthKey$

  public handleGoogleButtonClick(response: Omit<CredentialResponse, 'select_by'>): void {
    this.segmentService.trackEvent({
      name: SegmentEvents.BUTTON_CLICKED,
      properties: {
        name: 'Google Login',
        text: 'Sign in with Google',
        action: 'Click'
      }
    })
    this.googleSubmitting = true
    this.googleAuthErrorSubject.next(undefined)

    // TODO: we are not entirely sure of what the error message looks like from Google
    // @see {@link https://flocasts.atlassian.net/browse/CXP-2284}
    // The types package used is incomplete @see {@link https://developers.google.com/identity/gsi/web/reference/js-reference#ux_mode}
    // @ts-expect-error
    if (response.error) {
      // @ts-expect-error
      this.googleAuthErrorSubject.next(response.error)
    }

    this.auth
      .googleLogin(response.credential)
      .pipe(
        filter(coreApiResponse => !!coreApiResponse.user),
        tap(coreApiResponse => {
          const createdAt = Number(coreApiResponse.user.analyticsData.created_at)
          if (isNewUser(createdAt)) {
            this.segmentService.track(SegmentEvents.ACCOUNT_CREATED, {
              email: coreApiResponse.user.email,
              google_social: true
            })
          }
          this.handleGoogleLoginSuccess(coreApiResponse.user, true)
          this.googleSubmitting = false
        }),
        catchError(err => {
          this.loggingService.error(err.message, err.error)
          this.googleSubmitting = false
          return of(undefined)
        }),
        takeUntil(this.ngOnDestroy$)
        // TODO: refactor out .subscribe(), @see {@link https://flocasts.atlassian.net/browse/CXP-2284}
      )
      .subscribe()
  }
  public isBrowser = this.platformService.isBrowser

  public ngOnInit() {
    this.canonicalURL$.pipe(take(1)).subscribe((url: string) => {
      this.seoService.setCanonicalUrl(url)
    })
  }

  public ngOnDestroy(): void {
    this.seoService.removeCanonicalUrl()
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }
}
